(() => {
  if (customElements.get('oka-recovery-ugc')) return;

  customElements.define('oka-recovery-ugc', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-oka-ugc-track]');
      this.cards = [...this.querySelectorAll('[data-oka-ugc-card]')];
      this.navigation = this.querySelector('[data-oka-ugc-navigation]');
      this.previous = this.querySelector('[data-oka-ugc-previous]');
      this.next = this.querySelector('[data-oka-ugc-next]');
      this.position = this.querySelector('[data-oka-ugc-position]');
      if (!this.track || !this.cards.length) return;

      this.addEventListener('click', event => {
        const play = event.target.closest('[data-oka-ugc-play], [data-oka-ugc-retry]');
        if (!play || !this.contains(play)) return;
        event.preventDefault();
        this.playCard(play.closest('[data-oka-ugc-card]'), play.hasAttribute('data-oka-ugc-retry'));
      }, { signal });
      this.addEventListener('error', event => {
        if (!(event.target instanceof HTMLVideoElement)) return;
        const card = event.target.closest('[data-oka-ugc-card]');
        card.querySelector('[data-oka-ugc-status]').textContent = 'This video couldn’t load. Please try again or open it below.';
        card.querySelector('[data-oka-ugc-error]').hidden = false;
      }, { capture: true, signal });
      this.addEventListener('playing', event => {
        if (event.target instanceof HTMLVideoElement) {
          event.target.closest('[data-oka-ugc-card]').querySelector('[data-oka-ugc-error]').hidden = true;
        }
      }, { capture: true, signal });

      document.addEventListener('play', event => {
        if (event.target instanceof HTMLVideoElement && event.target.closest('oka-recovery-ugc')) {
          this.pauseVideos(event.target);
        }
      }, { capture: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
      }, { signal });

      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateNavigation();
        });
      }, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target instanceof HTMLVideoElement || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.currentIndex() + (event.key === 'ArrowRight' ? 1 : -1);
        this.scrollToCard(index);
      }, { signal });

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) entry.target.querySelector('video')?.pause();
          });
        }, { threshold: 0 });
        this.cards.forEach(card => this.visibilityObserver.observe(card));
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.updateNavigation(), { signal });
      }
      this.updateNavigation();
    }

    pauseVideos(except) {
      this.querySelectorAll('video').forEach(video => {
        if (video !== except) video.pause();
      });
    }

    playCard(card, retry = false) {
      if (!card) return;
      const error = card.querySelector('[data-oka-ugc-error]');
      const status = card.querySelector('[data-oka-ugc-status]');
      const showError = message => {
        if (!this.isConnected) return;
        status.textContent = message;
        error.hidden = false;
      };
      error.hidden = true;
      let video = card.querySelector('video');
      if (!video) {
        const template = card.querySelector('[data-oka-ugc-template]');
        if (!template?.content.querySelector('video')) {
          showError('This video is unavailable right now. You can open it using the link below.');
          return;
        }
        const fragment = template.content.cloneNode(true);
        video = fragment.querySelector('video');
        card.querySelector('[data-oka-ugc-media]').append(fragment);
        card.querySelector('[data-oka-ugc-play]').hidden = true;
      }
      this.pauseVideos(video);
      if (retry) video.load();
      video.focus({ preventScroll: true });
      const playing = video.play();
      playing?.catch(cause => {
        if (cause.name === 'AbortError') return;
        showError(cause.name === 'NotAllowedError' ? 'Use the play control on the video to start playback.' : 'This video couldn’t play. Please try again or open it below.');
      });
    }

    currentIndex() {
      const trackLeft = this.track.getBoundingClientRect().left;
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      let nearest = 0;
      let distance = Infinity;
      this.cards.forEach((card, index) => {
        const delta = Math.abs(card.getBoundingClientRect().left - trackLeft - padding);
        if (delta < distance) { distance = delta; nearest = index; }
      });
      return nearest;
    }

    updateNavigation() {
      if (!this.navigation) return;
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.navigation.hidden = !overflow;
      if (!overflow) return;
      const index = this.currentIndex();
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
      const label = `${index + 1} / ${this.cards.length}`;
      if (this.position.textContent !== label) this.position.textContent = label;
    }

    move(direction) {
      this.scrollToCard(this.currentIndex() + direction);
    }

    scrollToCard(index) {
      const card = this.cards[Math.max(0, Math.min(index, this.cards.length - 1))];
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      const left = this.track.scrollLeft + card.getBoundingClientRect().left - this.track.getBoundingClientRect().left - padding;
      this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    disconnectedCallback() {
      this.pauseVideos();
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.controller = null;
    }
  });
})();
